import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { patientsService } from '@/services/patients';
import { getApiErrorMessage } from '@/lib/api';
import { Plus } from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function PatientsPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({
    name: '',
    age: '',
    gender: '',
    email: '',
    phone: '',
    bloodGroup: '',
    address: '',
  });

  const fetchPatientList = async () => {
    const response = await patientsService.getAll();
    setPatients(response.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchPatientList();
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const columns = [
    {
      key: 'name',
      header: 'Patient',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={row.avatar} alt={value} />
            <AvatarFallback>{value?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{value}</p>
            <p className="text-xs text-muted-foreground">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'age',
      header: 'Age',
      sortable: true,
    },
    {
      key: 'gender',
      header: 'Gender',
      sortable: true,
    },
    {
      key: 'bloodGroup',
      header: 'Blood Group',
      sortable: true,
      render: (value) => (
        <Badge variant="outline">{value}</Badge>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
    },
    {
      key: 'lastVisit',
      header: 'Last Visit',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <Badge variant={value === 'Active' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-10 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Patients</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage patient records and information
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Patient</DialogTitle>
              <DialogDescription>
                Enter patient information to create a new record
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="patient-name">Full Name</Label>
                <Input
                  id="patient-name"
                  placeholder="John Smith"
                  value={newPatient.name}
                  onChange={(e) => setNewPatient((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="patient-age">Age</Label>
                  <Input
                    id="patient-age"
                    type="number"
                    placeholder="45"
                    value={newPatient.age}
                    onChange={(e) => setNewPatient((p) => ({ ...p, age: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="patient-gender">Gender</Label>
                  <Input
                    id="patient-gender"
                    placeholder="Male"
                    value={newPatient.gender}
                    onChange={(e) => setNewPatient((p) => ({ ...p, gender: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="patient-email">Email</Label>
                <Input
                  id="patient-email"
                  type="email"
                  placeholder="john@email.com"
                  value={newPatient.email}
                  onChange={(e) => setNewPatient((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="patient-phone">Phone</Label>
                <Input
                  id="patient-phone"
                  placeholder="+1 234 567 8900"
                  value={newPatient.phone}
                  onChange={(e) => setNewPatient((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="patient-blood">Blood group</Label>
                <Input
                  id="patient-blood"
                  placeholder="A+"
                  value={newPatient.bloodGroup}
                  onChange={(e) => setNewPatient((p) => ({ ...p, bloodGroup: e.target.value }))}
                />
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    await patientsService.create({
                      name: newPatient.name,
                      age: Number(newPatient.age),
                      gender: newPatient.gender,
                      email: newPatient.email || undefined,
                      phone: newPatient.phone || undefined,
                      bloodGroup: newPatient.bloodGroup || undefined,
                      address: newPatient.address || undefined,
                    });
                    toast.success('Patient added successfully');
                    setIsDialogOpen(false);
                    setNewPatient({
                      name: '',
                      age: '',
                      gender: '',
                      email: '',
                      phone: '',
                      bloodGroup: '',
                      address: '',
                    });
                    await fetchPatientList();
                  } catch (error) {
                    toast.error(getApiErrorMessage(error));
                  }
                }}
              >
                Add Patient
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable
        data={patients}
        columns={columns}
        searchPlaceholder="Search patients..."
        onRowClick={(row) => navigate(`/patients/${row.id}`)}
      />
    </div>
  );
}